import React, { useState, useRef, useEffect } from 'react'
import { MuiModal } from '@pinchenterprisesnpm/friday-ui'
import { faChevronDown } from '@fortawesome/pro-light-svg-icons'
import { faClock } from '@fortawesome/pro-solid-svg-icons'
import { isMobile } from '@src/helpers/helperFunctions'
import Popup, { PopupFooter } from '@src/ui/components/common/TimePicker/Popup'
import {
  normalizeValue,
  Portal,
  createAndAppendPopupElement,
  create,
} from '@src/ui/components/common/TimePicker/utils'
import { TTimePickerValueObject } from '@src/ui/components/common/TimePicker/TimePicker'
import {
  FloatingLabel,
  TriggerContainer,
  TriggerContent,
  TimeText,
  LeftIconWrapper,
  StyledIcon,
  IconWrapper,
} from './custom-time-picker.styles'

const { Header, Content, Footer } = MuiModal

interface CustomTimePickerProps {
  value: string
  onChange: (value: string) => void
  label?: string
  hasError?: boolean
  isDisabled?: boolean
  width?: string
  headerText?: string
}

// Static popup element for desktop
let popupElement: HTMLDivElement | null = null
let popperInstance: any = null

const getPopupElement = () => {
  if (!popupElement) {
    popupElement = createAndAppendPopupElement()
  }
  return popupElement
}

export const CustomTimePicker: React.FC<CustomTimePickerProps> = ({
  value,
  onChange,
  label = 'Select Time',
  hasError = false,
  isDisabled = false,
  width,
  headerText = 'Select Time',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const hasValue = !!value
  const isMobileView = isMobile()
  const normalizedValue = normalizeValue(value)

  // Setup popup positioning for desktop
  useEffect(() => {
    if (isOpen && !isMobileView && triggerRef.current) {
      const popup = getPopupElement()
      if (popperInstance) {
        popperInstance.destroy()
      }
      popperInstance = create(triggerRef.current, popup)
    }

    return () => {
      if (popperInstance && !isOpen) {
        popperInstance.destroy()
        popperInstance = null
      }
    }
  }, [isOpen, isMobileView])

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const isTimePickerClick = target.closest('.timePickerTrigger, .timePickerPopup')

      if (!isTimePickerClick && isOpen) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      if (isOpen) {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  const handleTriggerClick = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen)
    }
  }

  const closePopup = () => setIsOpen(false)

  const valueToString = (value: TTimePickerValueObject) => {
    const { hour, minute, period } = value
    return `${hour}:${minute} ${period}`
  }

  const changeValues = (values: Partial<TTimePickerValueObject>) => {
    const ret = {
      ...normalizedValue,
      ...values,
    }
    onChange(valueToString(ret))
  }

  const togglePeriod = () => {
    const { period } = normalizedValue
    const newPeriod = period === 'AM' ? 'PM' : 'AM'
    const ret = {
      ...normalizedValue,
      period: newPeriod,
    }
    onChange(valueToString(ret))
  }

  return (
    <>
      <TriggerContainer
        ref={triggerRef}
        isFocused={isOpen}
        hasError={hasError}
        onClick={handleTriggerClick}
        className='timePickerTrigger'
        isDisabled={isDisabled}
        style={{ width: width || '200px' }}
      >
        <FloatingLabel hasValue={hasValue} isFocused={isOpen}>
          {label}
        </FloatingLabel>
        <TriggerContent className='timePickerTrigger'>
          <LeftIconWrapper className='timePickerTrigger'>
            <StyledIcon icon={faClock} className='timePickerTrigger' />
          </LeftIconWrapper>
          <TimeText className='timePickerTrigger'>{value || ''}</TimeText>
          <IconWrapper className='timePickerTrigger'>
            <StyledIcon icon={faChevronDown} className='timePickerTrigger' />
          </IconWrapper>
        </TriggerContent>
      </TriggerContainer>

      {/* Desktop popup */}
      {isOpen && !isMobileView && (
        <Portal to={getPopupElement()}>
          <Popup
            value={normalizedValue}
            changeValue={() => {}} // Not used in this implementation
            changeValues={changeValues}
            togglePeriod={togglePeriod}
          />
        </Portal>
      )}

      {/* Mobile modal */}
      {isMobileView && (
        <MuiModal isOpen={isOpen} closeModal={closePopup} fullWidth maxWidth='xs'>
          <Header title={headerText} />
          <Content>
            <Popup
              value={normalizedValue}
              changeValue={() => {}} // Not used in this implementation
              changeValues={changeValues}
              togglePeriod={togglePeriod}
              modal
            />
          </Content>
          <Footer>
            <PopupFooter closeModal={closePopup} />
          </Footer>
        </MuiModal>
      )}
    </>
  )
}
